/* ---------------------------------------------------------------------------
   The fly-around map: MapLibre GL, terrain, and the decks standing on it.

   The third of the three views, and the one that answers a different question from
   the other two. The flat map says where the network is; the Site view says how one
   crossing sits between its banks; this one you tip over and fly along, with every
   structure extruded on real terrain — "what is this stretch of road actually like".

   IT IS A DEPENDENCY, AND THAT WAS THE ONLY REASON TO HESITATE. MapLibre GL JS is
   BSD-licensed, free, keyless with free tiles, and about 1 MB minified against a
   register bundle of 127 kB. It is imported dynamically, so nobody pays for it
   until they press Fly, and Vite gives it its own chunk. What it buys that the
   hand-rolled layers cannot is a camera: pitch, bearing, inertia, terrain-aware
   picking and a projection that stays correct while all of that moves.

   WHAT IT DRAWS THE BRIDGE WITH is `fill-extrusion` on the register's own recorded
   footprint, not the modeller's geometry — see the note on `deckFeatures` in
   ./flyStyle.ts for why, which is the most important comment in this feature.

   THE SAME MOUNTING ARRANGEMENT as the Site view and the modeller's frames: the
   library owns a container this module creates once and moves into whatever slot the
   current render put on screen. A WebGL map cannot be rebuilt by a reconciler.
   --------------------------------------------------------------------------- */

import type { BridgeView, MapBase } from '../data/types.ts';
import { actions } from '../core/store.ts';
import { located } from './nepalMap.ts';
import { deckFeatures, deckLayer, flyStyle, TERRAIN } from './flyStyle.ts';
import { operationalStatus } from '../data/posting.ts';

/** Minimal surface of the bits of MapLibre this module touches. */
interface FlyMap {
  addSource(id: string, source: Record<string, unknown>): void;
  addLayer(layer: Record<string, unknown>): void;
  setTerrain(terrain: Record<string, unknown> | null): void;
  addControl(control: unknown, position?: string): void;
  on(event: string, fn: (e: unknown) => void): void;
  once(event: string, fn: () => void): void;
  remove(): void;
  resize(): void;
  flyTo(options: Record<string, unknown>): void;
  getSource(id: string): { setData(data: unknown): void } | undefined;
}

let host: HTMLDivElement | null = null;
let map: FlyMap | null = null;
let showing: { base: MapBase; revision: number } | null = null;
let markers: { remove(): void }[] = [];
let building = 0;

/** What the card says while this is coming up or has failed. */
export interface FlyStatus {
  state: 'idle' | 'loading' | 'ready' | 'failed';
  message: string;
}

let status: FlyStatus = { state: 'idle', message: '' };
let onStatus: (s: FlyStatus) => void = () => {};

export function wireFlyStatus(fn: (s: FlyStatus) => void): void {
  onStatus = fn;
}

export function flyStatus(): FlyStatus {
  return status;
}

function setStatus(next: FlyStatus): void {
  status = next;
  onStatus(status);
}

/**
 * A marker for one structure: a dot that selects it, and flies to it.
 *
 * A DOM element rather than a symbol layer, because it needs no glyph server and
 * because it can carry the same two-step behaviour the other views have — the first
 * press brings the map to the structure, the second opens its record.
 */
function markerElement(b: BridgeView, onPick: () => void): HTMLElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'nmapfly__pin';
  /* Posted or closed (LC-23), as the flat map's pin says. */
  const status = operationalStatus(b);
  if (status.kind === 'posted' || status.kind === 'closed') el.classList.add(`nmapfly__pin--${status.kind}`);
  el.title = `${b.id} — ${b.name}\n${status.label}\nClick to fly to it, again to open the record`;
  el.setAttribute('aria-label', `${b.id} ${b.name}`);
  el.addEventListener('click', (e) => {
    e.stopPropagation();
    onPick();
  });
  return el;
}

/**
 * Build the map.
 *
 * Everything asynchronous happens before anything is added: the library, its
 * stylesheet, then one style object with both sources in it. Terrain is set on
 * `load` rather than in the style because a `raster-dem` source has to exist before
 * it can be named as terrain.
 */
async function build(slot: HTMLElement, list: readonly BridgeView[], base: MapBase, selectedId: string): Promise<void> {
  setStatus({ state: 'loading', message: 'Loading the map engine…' });

  /* The library and its own stylesheet — the latter for its controls and canvas
     positioning. Both inside this dynamic import so they share one chunk and nobody
     who never opens this view downloads either. The `?inline`-free CSS import is
     declared in ./maplibre.d.ts, since TypeScript has no idea what a stylesheet
     resolves to and Vite's own client types are not pulled into this project. */
  const [{ Map: MapLibreMap, Marker, NavigationControl, ScaleControl }] = await Promise.all([
    import('maplibre-gl'),
    import('maplibre-gl/dist/maplibre-gl.css'),
  ]);

  const container = document.createElement('div');
  container.className = 'nmapfly__canvas';
  slot.appendChild(container);

  const pins = located(list);
  // Somewhere worth starting: the selected structure if it has a position, else the
  // first that does. A pitched view at this zoom shows the crossing and its valley.
  const home = pins.find((b) => b.id === selectedId) ?? pins[0];

  const created = new MapLibreMap({
    container,
    style: flyStyle(base) as never,
    center: home ? [home.lng, home.lat] : [84.1, 28.2],
    zoom: home ? 14.5 : 7,
    pitch: 62,
    bearing: home?.deckBearing != null ? home.deckBearing - 90 : 0,
    // Our own credit line is rendered by the card, in the same place and the same
    // words as the other two views — see `credit` in nepalMap.ts.
    attributionControl: false,
    // Nothing in this app links to a shareable map position, so the hash would only
    // fight the register's own URL.
    hash: false,
  });
  const live = created as unknown as FlyMap;
  map = live;

  live.addControl(new NavigationControl({ visualizePitch: true }), 'bottom-left');
  live.addControl(new ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left');

  live.once('load', () => {
    /* Terrain after load, because the DEM source has to be there to be named. The
       same proxied tiles the Site view decodes by hand — see DEM_TILES. */
    try {
      live.setTerrain({ ...TERRAIN });
    } catch {
      // A device with no float-texture support, or a DEM that would not load.
      // The map is still a map; it is simply flat.
    }

    live.addSource('decks', { type: 'geojson', data: deckFeatures(list) });
    live.addLayer(deckLayer());

    for (const b of pins) {
      const marker = new Marker({ element: markerElement(b, () => pick(b, selectedId)) })
        .setLngLat([b.lng, b.lat])
        .addTo(created as never);
      markers.push(marker as unknown as { remove(): void });
    }

    setStatus({
      state: 'ready',
      message: `${pins.length} structure${pins.length === 1 ? '' : 's'} on terrain — drag to turn, right-drag to tilt.`,
    });
  });

  /* MapLibre reports failures here rather than by throwing, and they are not all
     the same kind of thing — which is the distinction this used to miss.

     A TILE that does not arrive is not a failed map: the rest of it is on screen
     and one blank square is worth a note, not a cover over the whole view. A STYLE
     that does not validate is a failed map, because MapLibre rejects the style
     whole — no imagery, no elevation, no decks, not one request — and the thing on
     screen is an empty canvas with a scale bar on it.

     This handler reported both as "ready, some tiles did not load", so an invalid
     style put up a blank grey box, said nothing in the console, and left the view
     looking like a rendering bug on the user's machine. Now the message it was
     given is the message it passes on, and a style error is stated as one. */
  live.on('error', (e: unknown) => {
    const message = String(
      (e as { error?: { message?: string } })?.error?.message ?? (e as { message?: string })?.message ?? e,
    );
    /* A style-validation error names the part of the style it failed on and where
       in it — `layers[1].type: expected one of [...]`, `sources.dem: ...` — which
       is a shape a tile failure never has. */
    const fatal = /^(layers|sources|sky|terrain|light|glyphs|sprite|projection)[[.:]/.test(message);
    if (fatal) {
      setStatus({ state: 'failed', message: `The map style was rejected: ${message}` });
      return;
    }
    if (status.state === 'loading') {
      setStatus({ state: 'ready', message: 'Some tiles did not load; the map is incomplete.' });
    }
  });
}

/** First click flies to a structure and selects it; the second opens the record. */
function pick(b: BridgeView & { lat: number; lng: number }, selectedId: string): void {
  if (b.id === selectedId) {
    actions.openBridge(b.id);
    return;
  }
  actions.selectBridge(b.id);
  map?.flyTo({
    center: [b.lng, b.lat],
    zoom: 15.2,
    pitch: 62,
    ...(b.deckBearing != null ? { bearing: b.deckBearing - 90 } : {}),
    duration: 1200,
  });
}

function dispose(): void {
  for (const marker of markers) marker.remove();
  markers = [];
  map?.remove();
  map = null;
  host = null;
  showing = null;
}

/**
 * Reconcile the fly map with state, after the render.
 *
 * Rebuilt only on a change of base map or of the register itself; a repaint for
 * anything else leaves the camera exactly where the user put it, which on a map you
 * fly around is the whole point. A change of selection moves the camera instead of
 * rebuilding — see `pick`.
 */
export function syncFlyMap(
  wanted: { list: readonly BridgeView[]; base: MapBase; selectedId: string; revision: number } | null,
): void {
  const slot = document.querySelector<HTMLDivElement>('.nmapfly__slot');

  if (!wanted || !slot) {
    if (map) dispose();
    /* Cleared whether or not there was a map to dispose of: a build that FAILED
       leaves `showing` standing (see the catch below), and it is leaving this mode
       that makes the next press of Fly a fresh attempt rather than the same
       failure remembered. */
    host = null;
    showing = null;
    if (status.state !== 'idle') setStatus({ state: 'idle', message: '' });
    return;
  }

  /* Same slot, same data: nothing to do — and `showing` rather than `map` is what
     answers that, because a build in flight has not assigned `map` yet. This is
     re-entered DURING the build: the first thing build() does is set the loading
     status, which repaints the shell, and a repaint ends by calling this. Keyed on
     `map` it would find none, start the build again, and recurse until the stack
     gave out — a frozen tab the moment anyone pressed Fly. `showing` is set before
     the build starts, so the re-entrant call finds the build it is already inside
     and lets it be. */
  if (host === slot && showing?.base === wanted.base && showing.revision === wanted.revision) {
    // The deck source may still have moved under it if a structure was placed by
    // hand, which is what `revision` catches.
    return;
  }

  if (map) dispose();
  host = slot;
  showing = { base: wanted.base, revision: wanted.revision };

  const token = ++building;
  void build(slot, wanted.list, wanted.base, wanted.selectedId).catch((err: unknown) => {
    if (token !== building) return;
    /* `showing` is deliberately LEFT SET. Clearing it would make the next repaint
       — the one this very status change is about to cause — see a slot with no map
       and start the whole build again, forever. A failure is reported once and
       stands until something actually changes: the base map, the register, or a
       trip out of this mode and back, each of which moves `showing` on its own. */
    setStatus({
      state: 'failed',
      message:
        err instanceof Error
          ? `The fly-around map could not be built: ${err.message}`
          : 'The fly-around map could not be built.',
    });
  });
}
